<template>
    <div class="deq-reset stopm-container">
        <div class="profile-header" v-if="profileLink">
            <a :href="profileLink" target="_blank" :title="'Open ' + profileName + '\'s profile in a new tab'">
                <div class="profile-name">{{ profileName }}</div>
                <div class="profile-handle">
                    <span v-if="isTwitter" class="twitter-at">@</span>
                    <i v-else-if="profileIconClass" :class="profileIconClass"></i>
                    {{ profileHandle }}
                </div>
            </a>
        </div>
        <div class="profile-metrics-container">
            <!-- Instagram -->
            <div v-if="isInstagram" :class="{'single-style': isSingleProfile, 'multi-style': !isSingleProfile}">
                <div class="stat-box highlight">
                    <span class="number">{{ formatNumber(followerCount) }}</span> followers
                    <div class="under-stats">
                        <i v-if="getArrowClass(followerDelta)" :class="getArrowClass(followerDelta)"></i>{{ formatNumber(followerDelta) }} / ({{ formatPercentage(followerPercent) }})
                    </div>
                </div>
            </div>

            <!-- Facebook -->
            <div v-if="isFacebook" :class="{'single-style': isSingleProfile, 'multi-style': !isSingleProfile}">
                <div class="stat-box highlight">
                    <span class="number">{{ formatNumber(followerCount) }}</span> followers
                    <div class="under-stats">
                        <i v-if="getArrowClass(followerDelta)" :class="getArrowClass(followerDelta)"></i>{{ formatNumber(followerDelta) }} / ({{ formatPercentage(followerPercent) }})
                    </div>
                </div>
                <div class="stat-box highlight">
                    <span class="number">{{ formatNumber(likeCount) }}</span> likes
                    <div class="under-stats">
                        <i v-if="getArrowClass(likesDelta)" :class="getArrowClass(likesDelta)"></i>{{ formatNumber(likesDelta) }} / ({{ formatPercentage(likesPercent) }})
                    </div>
                </div>
            </div>

            <!-- TikTok -->
            <div v-if="isTikTok" :class="{'single-style': isSingleProfile, 'multi-style': !isSingleProfile}">
                <div class="stat-box highlight">
                    <span class="number">{{ formatNumber(followerCount) }}</span> followers
                    <div class="under-stats">
                        <i v-if="getArrowClass(followerDelta)" :class="getArrowClass(followerDelta)"></i>{{ formatNumber(followerDelta) }} / ({{ formatPercentage(followerPercent) }})
                    </div>
                </div>
                <div class="stat-box highlight">
                    <span class="number">{{ formatNumber(likeCount) }}</span> likes
                    <div class="under-stats">
                        <i v-if="getArrowClass(likesDelta)" :class="getArrowClass(likesDelta)"></i>{{ formatNumber(likesDelta) }} / ({{ formatPercentage(likesPercent) }})
                    </div>
                </div>
            </div>

            <!-- LinkedIn -->
            <div v-if="isLinkedIn" :class="{'single-style': isSingleProfile, 'multi-style': !isSingleProfile}">
                <div class="stat-box highlight">
                    <span class="number">{{ formatNumber(followerCount) }}</span> followers
                    <div class="under-stats">
                        <i v-if="getArrowClass(followerDelta)" :class="getArrowClass(followerDelta)"></i>{{ formatNumber(followerDelta) }} / ({{ formatPercentage(followerPercent) }})
                    </div>
                </div>
            </div>

            <!-- YouTube -->
            <div v-if="isYouTube" :class="{'single-style': isSingleProfile, 'multi-style': !isSingleProfile}">
                <div class="stat-box">
                    <span class="number">{{ formatNumber(followerCount) }}</span> subscribers
                    <div class="under-stats">
                        <i v-if="getArrowClass(followerDelta)" :class="getArrowClass(followerDelta)"></i>{{ formatNumber(followerDelta) }} / ({{ formatPercentage(followerPercent) }})
                    </div>
                </div>
                <div class="stat-box">
                    <span class="number">{{ formatNumber(videoCount) }}</span> videos
                    <div class="under-stats">
                        <i v-if="getArrowClass(statusDelta)" :class="getArrowClass(statusDelta)"></i>{{ formatNumber(statusDelta) }} / ({{ formatPercentage(statusPercent) }})
                    </div>
                </div>
            </div>

            <!-- Twitter -->
            <div v-if="isTwitter" :class="{'single-style': isSingleProfile, 'multi-style': !isSingleProfile}">
                <div class="stat-box">
                    <span class="number">{{ formatNumber(followerCount) }}</span> followers
                    <div class="under-stats">
                        <i v-if="getArrowClass(followerDelta)" :class="getArrowClass(followerDelta)"></i>{{ formatNumber(followerDelta) }} / ({{ formatPercentage(followerPercent) }})
                    </div>
                </div>
                <div class="stat-box">
                    <span class="number">{{ formatNumber(followingCount) }}</span> following
                    <div class="under-stats">
                        <i v-if="getArrowClass(followingDelta)" :class="getArrowClass(followingDelta)"></i>{{ formatNumber(followingDelta) }} / ({{ formatPercentage(followingPercent) }})
                    </div>
                </div>
                <div class="stat-box">
                    <span class="number">{{ formatNumber(postCount) }}</span> tweets
                    <div class="under-stats">
                        <i v-if="getArrowClass(postDelta)" :class="getArrowClass(postDelta)"></i>{{ formatNumber(postDelta) }} / ({{ formatPercentage(postPercent) }})
                    </div>
                </div>
            </div>

            <!-- Graph container for mini graph -->
            <div class="graph-container"></div>
        </div>
    </div>
</template>


<script>
import * as d3 from 'd3';
import moment from 'moment';

export default {
    name: "SingleTextOnlineProfileMetric",

    props: {
        profileMetric: {
            type: Object,
            required: true
        },
        isSingleProfile: {
            type: Boolean,
            required: true
        }
    },

    computed: {
        latestStat() {
            // Get the last element in the values array (most recent stat)
            const values = this.profileMetric?.values || [];
            return values.length > 0 ? values[values.length - 1] : null;
        },

        firstStat() {
            // Get the first element in the values array (baseline for deltas)
            const values = this.profileMetric?.values || [];
            return values.length > 0 ? values[0] : null;
        },

        channel() {
            return this.latestStat?.channel || '';
        },

        isInstagram() {
            return this.channel.toLowerCase().includes('instagram');
        },

        isFacebook() {
            return this.channel.toLowerCase().includes('facebook');
        },

        isTikTok() {
            return this.channel.toLowerCase().includes('tiktok');
        },

        isLinkedIn() {
            return this.channel.toLowerCase().includes('linkedin');
        },

        isYouTube() {
            return this.channel.toLowerCase().includes('youtube');
        },

        isTwitter() {
            return this.channel.toLowerCase().includes('twitter');
        },

        // Follower metrics
        followerCount() {
            return this.latestStat?.followerCount || 0;
        },

        followerDelta() {
            return this.latestStat?.followerCountDelta || 0;
        },

        followerPercent() {
            if (!this.firstStat?.followerCount || this.firstStat.followerCount === 0) return 0;
            return (this.followerDelta / this.firstStat.followerCount) * 100;
        },

        // Like metrics
        likeCount() {
            return this.latestStat?.likeCount || 0;
        },

        likesDelta() {
            return this.latestStat?.likesDelta || 0;
        },

        likesPercent() {
            if (!this.firstStat?.likeCount || this.firstStat.likeCount === 0) return 0;
            return (this.likesDelta / this.firstStat.likeCount) * 100;
        },

        // Following metrics (Twitter)
        followingCount() {
            return this.latestStat?.followingCount || 0;
        },

        followingDelta() {
            return this.latestStat?.followingCountDelta || 0;
        },

        followingPercent() {
            if (!this.firstStat?.followingCount || this.firstStat.followingCount === 0) return 0;
            return (this.followingDelta / this.firstStat.followingCount) * 100;
        },

        // Post/Tweet metrics
        postCount() {
            return this.latestStat?.postCount || 0;
        },

        postDelta() {
            return this.latestStat?.postDelta || 0;
        },

        postPercent() {
            if (!this.firstStat?.postCount || this.firstStat.postCount === 0) return 0;
            return (this.postDelta / this.firstStat.postCount) * 100;
        },

        // Video metrics (YouTube)
        videoCount() {
            return this.latestStat?.videoCount || 0;
        },

        statusDelta() {
            return this.latestStat?.statusDelta || 0;
        },

        statusPercent() {
            if (!this.firstStat?.videoCount || this.firstStat.videoCount === 0) return 0;
            return (this.statusDelta / this.firstStat.videoCount) * 100;
        },

        // Profile information
        profileName() {
            return this.latestStat?.name || '';
        },

        profileHandle() {
            return this.latestStat?.handle || '';
        },

        profileHandleId() {
            return this.latestStat?.handleId || '';
        },

        profileLink() {
            if (!this.latestStat) return '';

            const channel = this.latestStat.channel;
            const handle = this.latestStat.handle;
            const name = this.latestStat.name;

            if (!channel) return '';

            switch (channel.toUpperCase()) {
                case 'TWITTER':
                    return 'https://twitter.com/' + name;
                case 'FACEBOOK':
                    return 'https://www.facebook.com/' + handle;
                case 'INSTAGRAM':
                    return 'https://instagram.com/' + name;
                case 'YOUTUBE':
                    return 'https://www.youtube.com/channel/' + handle;
                case 'LINKEDIN':
                    return 'https://www.linkedin.com/company/' + handle;
                case 'TIKTOK':
                    return 'https://www.tiktok.com/@' + name;
                default:
                    return '';
            }
        },

        profileIconClass() {
            if (!this.latestStat || !this.latestStat.channel) return '';

            const channel = this.latestStat.channel.toUpperCase();

            switch (channel) {
                case 'FACEBOOK':
                    return 'symbol-facebook-rect';
                case 'INSTAGRAM':
                    return 'symbol-instagram';
                case 'TWITTER':
                    return ''; // Twitter uses @ symbol instead of icon
                case 'LINKEDIN':
                    return 'symbol-linkedin-rect';
                case 'YOUTUBE':
                    return 'symbol-youtube';
                case 'TIKTOK':
                    return 'symbol-tiktok';
                case 'TELEGRAM':
                    return 'symbol-telegram';
                case 'WHATSAPP':
                    return 'symbol-whatsapp';
                case 'HELLOPETER':
                    return 'symbol-hellopeter';
                case 'GOOGLEBUSINESS':
                    return 'symbol-google-full';
                case 'TRUSTPILOT':
                    return 'symbol-trustpilot';
                default:
                    return '';
            }
        }
    },

    mounted() {
        this.renderGraphs();
    },

    methods: {
        formatNumber(num) {
            if (num == null) return '0';
            return Math.abs(num).toLocaleString();
        },

        formatPercentage(percent) {
            if (percent == null || !isFinite(percent)) return '0%';
            return Math.abs(percent).toFixed(1) + '%';
        },

        getArrowClass(delta) {
            if (delta > 0) return 'arrow up-arrow icon-up-bold';
            if (delta < 0) return 'arrow down-arrow icon-down-bold';
            return '';
        },

        renderGraphs() {
            const values = this.profileMetric?.values || [];
            if (values.length === 0) return;

            // Transform data for D3 - add time field
            const data = values.map(stat => ({
                ...stat,
                time: stat.captured ? moment(stat.captured).unix() : 0
            }));

            const container = this.$el.querySelector('.graph-container');
            if (!container) return;

            // Clear existing graphs
            container.innerHTML = '';

            // Check which metrics are available and should be shown
            const hasFollowerData = data.some(d => d.followerCount !== undefined);
            const hasLikesData = (this.isFacebook || this.isTikTok) && data.some(d => d.likeCount !== undefined);

            // Render follower graph for all channels
            if (hasFollowerData) {
                const followerDiv = document.createElement('div');
                followerDiv.className = 'metric-graph';
                const label = document.createElement('div');
                label.className = 'graph-label';
                label.textContent = this.isYouTube ? 'Subscribers' : 'Followers';
                followerDiv.appendChild(label);

                const chartDiv = document.createElement('div');
                chartDiv.className = 'area-graph';
                followerDiv.appendChild(chartDiv);
                container.appendChild(followerDiv);

                this.renderAreaChart(
                    chartDiv,
                    data,
                    d => d.time,
                    d => d.followerCount || 0
                );
            }

            // Render likes graph for Facebook and TikTok only
            if (hasLikesData) {
                const likesDiv = document.createElement('div');
                likesDiv.className = 'metric-graph';
                const label = document.createElement('div');
                label.className = 'graph-label';
                label.textContent = 'Likes';
                likesDiv.appendChild(label);

                const chartDiv = document.createElement('div');
                chartDiv.className = 'area-graph';
                likesDiv.appendChild(chartDiv);
                container.appendChild(likesDiv);

                this.renderAreaChart(
                    chartDiv,
                    data,
                    d => d.time,
                    d => d.likeCount || 0
                );
            }
        },

        renderAreaChart(element, data, x, y) {
            x = x || function(d) { return d.x; };
            y = y || function(d) { return d.y; };

            const width = 75;
            const height = 25;

            if (width === 0 && height === 0) {
                console.warn('Area chart given no width/height');
                return;
            }

            // Create SVG
            const svg = d3.select(element)
                .append('svg')
                .attr('width', width)
                .attr('height', height)
                .classed('bm', true)
                .classed('chart-sparkline', true);

            // Find data bounds
            const minX = x(data.reduce((min, d) => x(d) < x(min) ? d : min, data[0]));
            const maxX = x(data.reduce((max, d) => x(d) > x(max) ? d : max, data[0]));
            const minY = y(data.reduce((min, d) => y(d) < y(min) ? d : min, data[0]));
            const maxY = y(data.reduce((max, d) => y(d) > y(max) ? d : max, data[0]));

            // Create scales
            const xScale = d3.scaleLinear().domain([minX, maxX]).range([0, width]);
            const yScale = d3.scaleLinear().domain([minY, maxY]).range([height, 0]);

            // Create area generator
            const area = d3.area()
                .x(d => xScale(x(d)))
                .y0(height)
                .y1(d => yScale(y(d)));

            // Add area path with animation
            svg.append('path')
                .datum(data)
                .classed('bm-chart-sparkline-area', true)
                .attr('d', area)
                .style('fill', 'rgba(0, 128, 0, 0.6)')
                .style('stroke', 'green')
                .style('stroke-width', '1px')
                .style('opacity', 0)
                .transition()
                .duration(1000)
                .style('opacity', 1.0);
        }
    }
}
</script>


<style scoped lang="sass">
.deq-reset
    color: unset

.stopm-container
    display: flex
    flex-direction: row
    border-bottom: thin solid #d3d3d3

.profile-header
    display: flex

    a
        color: inherit
        text-decoration: none
        display: block
        margin: auto

        &:hover
            color: green

            .profile-name
                text-decoration: underline

    .profile-name
        font-size: 1.5em
        line-height: 1.2em
        font-weight: 500
        margin-bottom: 4px

    .profile-handle
        font-size: 0.9em
        color: #808080
        line-height: 1.2em

        i
            margin-right: 5px
            vertical-align: middle

.group1
    float: left
    padding-right: 16px

.single-style
    display: flex
    flex-direction: column

.multi-style
    display: flex
    flex-direction: row


.stat-box
    padding: 20px 5px 10px 20px
    font-size: 1em
    line-height: 1em

    .number
        font-size: 1.5em

    .under-stats
        margin-top: 2px

.highlight
    font-size: 1.1em

    .number
        font-size: 2em

.arrow
    font-size: 1.5em
    position: relative

.up-arrow
    color: green
    top: 2px

.down-arrow
    color: #ff0000
    top: 3px

.graph-container
    padding: 20px
    display: flex
    justify-content: space-around
    flex-wrap: wrap
    clear: both

.metric-graph
    display: flex
    flex-direction: column
    gap: 8px
    align-items: center

.graph-label
    font-size: 0.9em
    color: #666
    font-weight: 500

.area-graph
    width: 75px
    height: 25px
    display: inline-block

.bm-chart-sparkline-area
    fill: rgba(0, 128, 0, 0.6)
    stroke: green
    stroke-width: 1px
</style>